import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as ETVConstants from '../ETVConstants';

class RolesList extends React.Component {
  state = {
    isLoaded: false,
    groupedRoles: {},
    current_user: undefined,
    isSaving: false,
    expandedCategories: {}
  };

  accessLevelOrder = {
    standard: 1,
    basic: 1,
    premium: 2,
    enterprise: 3,
    oem: 4,
    admin: 5
  };

  componentDidMount() {
    this.loadUserRoles();
  }

  componentDidUpdate(prevProps) {
    if (this.props.user_id && this.props.user_id !== prevProps.user_id) {
      this.setState(
        {
          current_user: this.props.user_id,
          isLoaded: false,
          expandedCategories: {}
        },
        this.loadUserRoles
      );
    }
  }

  setContains(setItems, item) {
    return setItems?.some(r => r.trim() === item.trim());
  }

  createGroupedByCategory(allRoles, userRoles) {
    const roleList = Array.isArray(allRoles)
      ? allRoles.map(r => r.trim())
      : allRoles.split(',').map(r => r.trim());

    const userRoleList = Array.isArray(userRoles)
      ? userRoles.map(r => r.trim())
      : userRoles.split(',').map(r => r.trim());

    const uniqueRoles = Array.from(new Set(roleList));
    const grouped = {};

    for (const role of uniqueRoles) {
      const parts = role.split('.');
      if (parts.length < 3) continue;

      const category = parts[1];

      if (!grouped[category]) {
        grouped[category] = [];
      }

      grouped[category].push({
        name: role,
        isChecked: this.setContains(userRoleList, role),
        accessLevel: parts[0]
      });
    }

    return grouped;
  }

  loadUserRoles = () => {
    const { user_id, roles } = this.props;

    if (!user_id || !roles) return;

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    const url = `${ETVConstants.getServerBase()}/useradmin?user_id=${user_id}&action=get_roles`;

    fetch(url, {
      headers: {
        user_id: reqUserID,
        access_token: accessToken
      }
    })
      .then(res => res.json())
      .then(result => {
        const groupedMap = this.createGroupedByCategory(roles, result.roles);

        this.setState({
          groupedRoles: groupedMap,
          isLoaded: true
        });
      })
      .catch(error => {
        console.error('Failed to load user roles:', error);
        this.setState({ isLoaded: true });
      });
  };

  handleAccordionChange = category => (event, isExpanded) => {
    this.setState(prevState => ({
      expandedCategories: {
        ...prevState.expandedCategories,
        [category]: isExpanded
      }
    }));
  };

  handleToggle = roleName => {
    const updated = { ...this.state.groupedRoles };

    for (const category in updated) {
      updated[category] = updated[category].map(role =>
        role.name === roleName
          ? { ...role, isChecked: !role.isChecked }
          : role
      );
    }

    const selectedRoles = this.collectSelectedRoles(updated);

    this.setState({ groupedRoles: updated });
    this.saveRoles(selectedRoles);
  };

  handleToggleCategory = (category, checkAll) => {
    const updated = { ...this.state.groupedRoles };

    updated[category] = updated[category].map(role => ({
      ...role,
      isChecked: checkAll
    }));

    const selectedRoles = this.collectSelectedRoles(updated);

    this.setState(prevState => ({
      groupedRoles: updated,
      expandedCategories: {
        ...prevState.expandedCategories,
        [category]: true
      }
    }));

    this.saveRoles(selectedRoles);
  };

  collectSelectedRoles = groupedRoles => {
    const selectedRoles = [];

    for (const category in groupedRoles) {
      for (const role of groupedRoles[category]) {
        if (role.isChecked) {
          selectedRoles.push(role.name);
        }
      }
    }

    return selectedRoles;
  };

  saveRoles = selectedRoles => {
    const { user_id } = this.props;

    if (!user_id) return;

    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    this.setState({ isSaving: true });

    const trimmedRoles = selectedRoles.map(r => r.trim());
    const roleStr = `[${trimmedRoles.join(',')}]`;

    const url = `${ETVConstants.getServerBase()}/useradmin?action=set_roles&user_id=${user_id}&roles=${encodeURIComponent(roleStr)}`;

    fetch(url, {
      headers: {
        user_id: reqUserID,
        access_token: accessToken
      }
    })
      .then(res => res.json())
      .then(responseJson => {
        const updatedGrouped = this.createGroupedByCategory(
          this.props.roles,
          responseJson.roles
        );

        this.setState({
          groupedRoles: updatedGrouped,
          isSaving: false
        });
      })
      .catch(error => {
        console.error('Error saving roles:', error);
        this.setState({ isSaving: false });
      });
  };

  translateRole = roleName => {
    const name = roleName.substring(roleName.lastIndexOf('.') + 1);
    const t = ETVConstants.trans;

    switch (name) {
      case 'definition': return t('definition');
      case 'advertising': return t('advertising');
      case 'library': return t('content_library');
      case 'access_market_place': return t('access_to_market_place');
      case 'contribute_marketplace': return t('contribute_to_market_place');
      case 'playlist': return t('playlist');
      case 'create_text': return t('create_texts');
      case 'weather_info': return t('weather_information');
      case 'surveys': return t('create_surveys');
      case 'coupons': return t('create_coupons');
      case 'reservations': return t('create_reservations');
      case 'device_configuration': return t('devices_configuration');
      case 'remote_management': return t('remote_management_devices');
      case 'layout_designer': return t('layout_designer');
      case 'user_management': return t('user_management');
      case 'elevator_config': return t('elevator_interface_configuration');
      case 'elevator_statistics': return t('elevator_statistics');
      case 'about': return t('about');
      case 'cam': return t('camera');
      case 'card_management': return t('card_management');
      case 'advertising_approvals': return t('advertising_approvals');
      case 'advertising_definition': return t('advertising_definition');
      case 'general': return t('general');
      case 'audit_log': return t('audit_log');
      default: return '*[' + name + ']*';
    }
  };

  getAccessLevelIcon(level) {
    switch (level) {
      case 'premium': return '⭐';
      case 'enterprise': return '🏢';
      case 'admin': return '🛠️';
      case 'oem': return '🔌';
      case 'standard':
      default:
        return '';
    }
  }

  formatCategoryName(categoryKey) {
    const t = ETVConstants.trans;

    switch (categoryKey) {
      case 'advertising': return t('category_advertising');
      case 'building': return t('category_building');
      case 'device': return t('category_device');
      case 'content': return t('category_content');
      case 'elevator': return t('category_elevator');
      case 'usermgmt': return t('category_user_management');
      case 'security': return t('category_security');
      default:
        return categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);
    }
  }

  render() {
    const {
      groupedRoles,
      isLoaded,
      isSaving,
      expandedCategories
    } = this.state;

    if (!isLoaded) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <div>
        {isSaving && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              opacity: 0.7
            }}
          >
            Saving...
          </Typography>
        )}

        {Object.entries(groupedRoles).map(([category, roles]) => {
          const allChecked = roles.every(r => r.isChecked);
          const someChecked = roles.some(r => r.isChecked);

          const sortedRoles = [...roles].sort((a, b) => {
            const order = level => this.accessLevelOrder[level] || 999;
            return order(a.accessLevel) - order(b.accessLevel);
          });

          return (
            <Accordion
              key={category}
              expanded={!!expandedCategories[category]}
              onChange={this.handleAccordionChange(category)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  sx={{ margin: 0 }}
                  onClick={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  control={
                    <Checkbox
                      checked={allChecked}
                      indeterminate={someChecked && !allChecked}
                      onChange={e =>
                        this.handleToggleCategory(category, e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {this.formatCategoryName(category)}
                    </Typography>
                  }
                />
              </AccordionSummary>

              <AccordionDetails>
                <FormGroup>
                  {sortedRoles.map(role => (
                    <FormControlLabel
                      key={role.name}
                      control={
                        <Checkbox
                          checked={role.isChecked}
                          onChange={() => this.handleToggle(role.name)}
                        />
                      }
                      label={
                        <>
                          {this.translateRole(role.name)}{' '}
                          <span style={{ opacity: 0.7 }}>
                            {this.getAccessLevelIcon(role.accessLevel)}
                          </span>
                        </>
                      }
                    />
                  ))}
                </FormGroup>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    );
  }
}

export default RolesList;